import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { createAvatar } from '@dicebear/core';
import * as funEmoji from '@dicebear/fun-emoji';
import { RootStoreContext } from '../../stores/RootStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { Leaderboard, LeaderboardEntry, fetchAuthenticatedImage } from '../../api/API';
import './LeaderboardStep.css';

const Avatar: React.FC<{ entry: LeaderboardEntry }> = ({ entry }) => {
  const [errored, setErrored] = React.useState(false);
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  const fallbackAvatarUrl = React.useMemo(() => {
    return createAvatar(funEmoji, {
      seed: String(entry.userId || entry.firstName || 'eufooria-user'),
      backgroundColor: ['f2d3b1', 'e5f4fb', 'd1d4f9', 'ffd5dc', 'c0aede'],
    }).toDataUri();
  }, [entry.userId, entry.firstName]);

  React.useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setErrored(false);
    setObjectUrl(null);

    if (entry.avatarUrl) {
      fetchAuthenticatedImage(entry.avatarUrl).then(url => {
        if (cancelled) {
          if (url) window.URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          createdUrl = url;
          setObjectUrl(url);
        } else {
          setErrored(true);
        }
      });
    }

    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [entry.avatarUrl]);

  if (entry.avatarUrl && objectUrl && !errored) {
    return (
      <img
        className="lb-avatar"
        src={objectUrl}
        alt={entry.firstName}
        onError={() => setErrored(true)}
      />
    );
  }

  return (
    <img
      className="lb-avatar"
      src={fallbackAvatarUrl}
      alt={`${entry.firstName || 'User'} default avatar`}
    />
  );
};

const Row: React.FC<{ entry: LeaderboardEntry; isYou?: boolean }> = ({ entry, isYou }) => (
  <div className={`lb-row ${isYou || entry.currentUser ? 'lb-row-you' : ''}`}>
    <div className="lb-rank">#{entry.rank}</div>
    <Avatar entry={entry} />
    <div className="lb-name">
      {entry.firstName}
      {(isYou || entry.currentUser) && <span className="lb-you-chip">YOU</span>}
    </div>
    <div className="lb-count">
      {entry.classCount}
      <span className="lb-count-label"> classes</span>
    </div>
  </div>
);

const buildCta = (lb: Leaderboard, topN: number): string => {
  if (lb.currentUserRank != null && lb.currentUserRank <= topN) {
    return 'Secure your position — book one more class this week.';
  }
  if (lb.classesToTop > 0 && lb.classesToTop <= 3) {
    const n = lb.classesToTop;
    return `Ready for the push? You're only ${n} class${n === 1 ? '' : 'es'} away from the top ${topN}.`;
  }
  if (lb.ctaClassesNeeded != null && lb.ctaTargetTopN != null) {
    const n = lb.ctaClassesNeeded;
    return `You're only ${n} class${n === 1 ? '' : 'es'} away from getting into Top ${lb.ctaTargetTopN}.`;
  }
  return `You're ${lb.classesToTop} classes away from the top dancers!`;
};

const LeaderboardStep = observer(() => {
  const { registrationStore } = useContext(RootStoreContext);
  const { leaderboard, loadLeaderboard, clearSelectedItems, toSellingItems } = registrationStore;

  useEffect(() => {
    if (!leaderboard) {
      loadLeaderboard();
    }
  }, [loadLeaderboard]);

  const handleBack = () => {
    clearSelectedItems();
    toSellingItems();
  };

  if (!leaderboard) {
    return (
      <Container>
        <div className="text-center mt-4">Loading leaderboard...</div>
      </Container>
    );
  }

  const topN = leaderboard.topStudents.length;
  const showCurrentUserSection = leaderboard.currentUserEntry != null;

  return (
    <Container className="leaderboard-container">
      <div className="leaderboard-page-header">
        <button className="secondary-nav-button" onClick={handleBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>Back</span>
        </button>
      </div>

      <h2 className="leaderboard-title">
        <span role="img" aria-label="trophy">🏆</span> Most Active Dancers
      </h2>
      <div className="leaderboard-subtitle">
        {leaderboard.groupName} • Last {leaderboard.lookbackDays} Days
      </div>

      {(leaderboard.availableGroups?.length || 0) > 1 && (
        <div className="leaderboard-groups" role="group" aria-label="Dance styles">
          {leaderboard.availableGroups!.map(group => (
            <button key={group.styleId} type="button" className="leaderboard-group-pill"
              aria-pressed={group.styleId === leaderboard.groupStyleId}
              onClick={() => loadLeaderboard(group.styleId)}>
              {group.name}
            </button>
          ))}
        </div>
      )}

      <div className="leaderboard-list">
        {leaderboard.topStudents.map(entry => (
          <Row key={`top-${entry.userId}`} entry={entry} />
        ))}
      </div>

      {showCurrentUserSection && (
        <>
          <div className="lb-gap" aria-hidden="true">
            <div className="lb-gap-row" />
            <div className="lb-gap-row" />
          </div>
          <div className="leaderboard-list leaderboard-you-list">
            <Row entry={leaderboard.currentUserEntry!} isYou />
            {leaderboard.belowCurrentUser.map(entry => (
              <Row key={`below-${entry.userId}`} entry={entry} />
            ))}
          </div>
        </>
      )}

      <div className="leaderboard-cta">{buildCta(leaderboard, topN || 5)}</div>
    </Container>
  );
});

export default LeaderboardStep;
